import React, { useState } from "react";
import Car from "../../models/Car";
import { formatCurrency } from "../component/CarCard";
import Icons from "../component/Icons";
import SellVehicleModal from "../../ViewModel/component/SellVehicleModal";
import { carFromListing, loadCars, saveCar, stock } from "../../lib/stock";

interface AdminInventoryProps {
    inventory: Car[];
    setInventory: React.Dispatch<React.SetStateAction<Car[]>>;
    onSelectCar?: (car: Car) => void;
    onEditCar?: (car: Car) => void;
    isAdmin: boolean;
}

const AdminInventory: React.FC<AdminInventoryProps> = ({ inventory, setInventory, onSelectCar, onEditCar, isAdmin }) => {
    const [search, setSearch] = useState<string>('');
    const [filter, setFilter] = useState<string>('active');
    const [sellingCar, setSellingCar] = useState<Car | null>(null);

    const toggleArchive = async (id: string) => {
        const car = inventory.find(c => c.id === id);
        if (!car) return;
        const next = new Car({ ...car, archive: !car.archive });
        const saved = await saveCar(next);
        if (!saved) {
            console.error('Failed to update inventory:', stock.state.error);
            return;
        }
        setInventory(inventory.map(c => c.id === id ? new Car({ ...next, images: saved.images }) : c));
    };

    const toggleFeatured = (id: string) => {
        const car = inventory.find(c => c.id === id);
        if (!car) return;
        setInventory(inventory.map(c => c.id === id ? new Car({ ...c, featured: !c.featured }) : c));
    };

    const addNewCar = async () => {
        const created = await stock.createBlank();
        if (!created) {
            console.error('Failed to add car:', stock.state.error);
            return;
        }
        const newCar = carFromListing(created);
        setInventory(prev => [newCar, ...prev]);
        (onEditCar || onSelectCar)?.(newCar);
    };

    const filtered = inventory.filter((c: Car) => {
        const matchesSearch = `${c.year} ${c.make} ${c.model}`.toLowerCase().includes(search.toLowerCase());
        const isArchived = c.archive === true || String(c.archive) === "true";
        if (filter === 'active') return !isArchived && matchesSearch;
        if (filter === 'archived') return isArchived && matchesSearch;
        return matchesSearch;
    });

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-gray-900 leading-none">Inventory</h1>
                    <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest mt-1">Stock Control Portal</p>
                </div>
                <button 
                    className="w-full sm:w-auto bg-red-600 text-white px-8 py-3 rounded-xl font-black uppercase shadow-lg shadow-red-100 hover:bg-red-700 transition transform hover:-translate-y-1" 
                    onClick={addNewCar}
                >
                    + New Listing
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-8 p-5 flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                    <input 
                        type="text" 
                        placeholder="Search Make, Model, ID..." 
                        className="w-full border-2 border-gray-50 rounded-xl p-3 pl-10 bg-gray-50 focus:bg-white focus:border-red-600 outline-none transition font-medium" 
                        value={search} 
                        onChange={e => setSearch(e.target.value)} 
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    </div>
                </div>
                <select 
                    className="border-2 border-gray-50 rounded-xl p-3 bg-gray-50 focus:bg-white focus:border-red-600 outline-none transition font-bold text-gray-700" 
                    value={filter} 
                    onChange={e => setFilter(e.target.value)}
                >
                    <option value="active">Active Inventory</option>
                    <option value="archived">Archived / Sold</option>
                    <option value="all">All Records</option>
                </select>
            </div>

            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[800px]">
                        <thead>
                            <tr className="bg-gray-900 text-white">
                                <th className="p-5 text-[10px] font-black uppercase tracking-widest">Image</th>
                                <th className="p-5 text-[10px] font-black uppercase tracking-widest">Details</th>
                                <th className="p-5 text-[10px] font-black uppercase tracking-widest">Price</th>
                                <th className="p-5 text-[10px] font-black uppercase tracking-widest text-center">Status</th>
                                <th className="p-5 text-[10px] font-black uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map((car) => {
                                const isArchived = car.archive === true || String(car.archive) === "true";
                                return (
                                    <tr key={car.id} className="hover:bg-gray-50/50 transition group">
                                        <td className="p-5 w-24">
                                            <div className="w-20 h-14 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                                                {car.images?.[0] ? (
                                                    <img src={car.images[0]} className="w-full h-full object-cover" alt="thumbnail" />
                                                ) : (
                                                    <Icons.Car />
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-5">
                                            <div className="font-black text-gray-900 uppercase italic leading-none">{car.year} {car.make}</div>
                                            <div className="font-bold text-red-600 uppercase text-xs mt-1">{car.model}</div>
                                            <div className="text-[10px] text-gray-400 mt-2 font-mono uppercase">Stock ID: {car.id}</div>
                                        </td>
                                        <td className="p-5">
                                            <span className="font-black text-xl text-gray-900">{formatCurrency(car.price)}</span>
                                            <div className="text-[10px] text-gray-400 font-bold uppercase">{car.mileage} miles</div>
                                        </td>
                                        <td className="p-5 text-center">
                                            <div className="inline-flex flex-col items-center gap-1.5">
                                                <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${isArchived ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                                                    {isArchived ? 'Archived' : 'Active'}
                                                </span>
                                                {car.featured && !isArchived && (
                                                    <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-100 text-red-700">
                                                        Featured
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-5 text-right space-x-2">
                                            <span className="inline-flex items-center gap-2">
                                                <button 
                                                    onClick={() => (onEditCar || onSelectCar)?.(car)}
                                                    className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-900 hover:text-white rounded-lg transition text-xs font-bold uppercase"
                                                >
                                                    <Icons.Edit /> <span className="ml-2">Edit</span>
                                                </button>
                                                <button
                                                    onClick={() => toggleFeatured(car.id)}
                                                    title={car.featured ? 'Unpin from featured' : 'Pin as featured'}
                                                    aria-label={car.featured ? 'Unpin from featured' : 'Pin as featured'}
                                                    className={`inline-flex items-center px-4 py-2 rounded-lg transition text-xs font-bold uppercase ${
                                                        car.featured
                                                            ? 'bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-100'
                                                            : 'bg-gray-100 hover:bg-red-600 hover:text-white'
                                                    }`}
                                                >
                                                    <Icons.Pin /> <span className="ml-2">{car.featured ? 'Featured' : 'Feature'}</span>
                                                </button>
                                            </span>
                                            
                                            {isArchived ? (
                                                <button 
                                                    onClick={() => toggleArchive(car.id)} 
                                                    className="px-4 py-2 rounded-lg text-xs font-bold uppercase transition bg-amber-100 text-amber-700 hover:bg-amber-200"
                                                >
                                                    Restore
                                                </button>
                                            ) : (
                                                <button 
                                                    onClick={() => setSellingCar(car)} 
                                                    className="px-4 py-2 rounded-lg text-xs font-bold uppercase transition bg-green-600 text-white hover:bg-green-700 shadow-md shadow-green-100"
                                                >
                                                    Sell Vehicle
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {filtered.length === 0 && (
                    <div className="p-20 text-center flex flex-col items-center">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-4">
                            <Icons.Car />
                        </div>
                        <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">No matching records found</p>
                    </div>
                )}
            </div>

            {sellingCar && (
                <SellVehicleModal 
                    car={sellingCar} 
                    onClose={() => setSellingCar(null)} 
                    canFinalizeSale={isAdmin}
                    onSaleSuccess={async () => {
                        const cars = await loadCars();
                        if (cars) setInventory(cars);
                    }} 
                />
            )}
        </div>
    );
};

export default AdminInventory;
